/**
 * Footer — rebuilt from scratch, 28 Jul 2026.
 *
 * Rules for this file (do not break):
 *   1. EVERY internal link is a plain <a href="/route"> element.
 *      Not <Link>, not <button>, not <NavLink>. Just <a>.
 *      This guarantees the browser navigates via the URL, even if any
 *      React Router state ever gets confused post-deploy.
 *   2. External links use rel="noreferrer" target="_blank".
 *   3. All tap targets are min 44px tall on mobile (Apple/Google guideline).
 *   4. Terms & Conditions gets its own dedicated row for maximum visibility.
 *   5. No client-side routing dependencies — this footer works even if the
 *      React app hasn't mounted yet.
 */

import { useState } from "react";
import { Instagram, Youtube, Mail, Rss } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";

/* -------------------------------------------------------------- */
/* Link data — one source of truth. Add rows here, they appear    */
/* automatically. Every href starts with "/" so nothing ambiguous.*/
/* -------------------------------------------------------------- */
const EXPLORE_LINKS = [
    { href: "/gallery",      label: "Gallery" },
    { href: "/portfolio",    label: "Portfolio" },
    { href: "/nc500",        label: "NC500 Wishlist" },
    { href: "/map",          label: "Locations Map" },
    { href: "/subscription", label: "Subscribe — £1.89" },
    { href: "/blog",         label: "Journal" },
];

const STUDIO_LINKS = [
    { href: "/about",   label: "About" },
    { href: "/things",  label: "Things to do" },
    { href: "/book",    label: "Book a shoot" },
    { href: "/contact", label: "Contact" },
];

const LEGAL_LINKS = [
    { href: "/terms", label: "Terms & Conditions" },
];

/* -------------------------------------------------------------- */

function FooterLink({ href, label, testid }) {
    return (
        <a
            href={href}
            className="inline-block py-2 text-sm text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] transition-colors"
            data-testid={testid}
        >
            {label}
        </a>
    );
}

function FooterColumn({ heading, items, prefix }) {
    return (
        <div>
            <div className="overline mb-4">{heading}</div>
            <ul className="space-y-1">
                {items.map((it) => (
                    <li key={it.href}>
                        <FooterLink
                            href={it.href}
                            label={it.label}
                            testid={`footer-${prefix}-${it.href.slice(1) || "home"}`}
                        />
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function Footer() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setBusy(true);
        try {
            await api.post("/newsletter", { email });
            toast.success("You're on the list. Welcome aboard.");
            setEmail("");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <footer className="relative mt-32 border-t border-[var(--sb-border)] pt-20 pb-10 z-10" data-testid="site-footer">
            {/* ---- MAIN GRID ---- */}
            <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
                {/* Newsletter */}
                <div className="md:col-span-5">
                    <div className="overline mb-3" style={{ color: "#0055A4", fontWeight: 700 }}>
                        <span className="editorial-rule" style={{ background: "#0055A4" }} />
                        Newsletter
                    </div>
                    <h3 className="font-serif text-3xl lg:text-4xl tracking-tight mb-5 leading-tight">
                        Field notes <span className="italic" style={{ color: "#0055A4" }}>from above.</span>
                    </h3>
                    <p className="text-[var(--sb-text-dim)] mb-6 max-w-md leading-relaxed">
                        New flights, post-production breakdowns, and the occasional behind-the-scenes drop. One email a month.
                    </p>
                    <form onSubmit={subscribe} className="flex items-end gap-4 max-w-md" data-testid="newsletter-form">
                        <input
                            type="email"
                            required
                            placeholder="your@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="input-line flex-1"
                            data-testid="newsletter-email-input"
                        />
                        <button disabled={busy} className="btn-primary" data-testid="newsletter-submit">
                            {busy ? "…" : "Subscribe"}
                        </button>
                    </form>
                </div>

                {/* Explore */}
                <div className="md:col-span-3 md:col-start-7">
                    <FooterColumn heading="Explore" items={EXPLORE_LINKS} prefix="explore" />
                </div>

                {/* Studio */}
                <div className="md:col-span-3">
                    <FooterColumn heading="Studio" items={STUDIO_LINKS} prefix="studio" />
                    <div className="flex gap-4 mt-6">
                        <a
                            href="https://instagram.com"
                            target="_blank"
                            rel="noreferrer"
                            className="text-[var(--sb-text-muted)] hover:text-[var(--sb-brand)] transition-colors py-1"
                            data-testid="footer-social-instagram"
                            aria-label="Instagram"
                        >
                            <Instagram size={18} />
                        </a>
                        <a
                            href="https://youtube.com"
                            target="_blank"
                            rel="noreferrer"
                            className="text-[var(--sb-text-muted)] hover:text-[var(--sb-brand)] transition-colors py-1"
                            data-testid="footer-social-youtube"
                            aria-label="YouTube"
                        >
                            <Youtube size={18} />
                        </a>
                        <a
                            href="mailto:hello@dtscotslad.com"
                            className="text-[var(--sb-text-muted)] hover:text-[var(--sb-brand)] transition-colors py-1"
                            data-testid="footer-social-email"
                            aria-label="Email"
                        >
                            <Mail size={18} />
                        </a>
                        <a
                            href="/api/feed.xml"
                            target="_blank"
                            rel="noreferrer"
                            className="text-[var(--sb-text-muted)] hover:text-[var(--sb-brand)] transition-colors py-1"
                            data-testid="footer-social-rss"
                            aria-label="Atom / RSS feed"
                            title="Subscribe via Atom / RSS"
                        >
                            <Rss size={18} />
                        </a>
                    </div>
                </div>
            </div>

            {/* ---- LEGAL ROW (dedicated, unmissable) ---- */}
            <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-14" data-testid="footer-legal-row">
                <div className="pt-6 border-t border-[var(--sb-border-subtle)] flex flex-wrap items-center gap-x-6 gap-y-2">
                    <span className="text-xs uppercase tracking-[0.24em] text-[var(--sb-text-muted)]">Legal</span>
                    {LEGAL_LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            className="inline-block py-3 md:py-2 text-sm text-[#0055A4] hover:text-[var(--sb-text)] transition-colors underline underline-offset-4"
                            data-testid={`footer-legal-${l.href.slice(1)}`}
                        >
                            {l.label}
                        </a>
                    ))}
                </div>
            </div>

            {/* ---- COPYRIGHT ROW ---- */}
            <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-6 pt-6 border-t border-[var(--sb-border-subtle)] flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs text-[var(--sb-text-muted)]">
                <div data-testid="footer-copyright-main">
                    © {new Date().getFullYear()} DTScotslad · All footage flown under UK CAA regulations
                </div>
                <div data-testid="footer-copyright-rights">
                    Photography &amp; films © Mark. All rights reserved.
                </div>
            </div>
        </footer>
    );
}
